import {
  ReactNode,
  createContext,
  useState,
  useCallback,
  useContext
} from 'react'
import { axios } from 'lib'
import { uniqueId } from 'lodash'
import fileSize from 'filesize'
import { toast } from 'react-toastify'
import { normalizeFileName } from 'utils'

export interface IFile {
  id: string
  name: string
  path: string
  filename: string
  readableSize: string
  uploaded?: boolean
  preview?: string
  file: File | null
  progress?: number
  error?: boolean
  url: string
}

interface IFileContextData {
  uploadedFiles: IFile[]
  deleteFile(id: string): void
  handleUpload(file: any): void
}

interface IFileUploadProvider {
  children: ReactNode
}

const FileContext = createContext<IFileContextData>({} as IFileContextData)

const FileUploadProvider = ({ children }: IFileUploadProvider) => {
  const [uploadedFiles, setUploadedFiles] = useState<IFile[]>([])

  const updateFile = useCallback((id, data) => {
    setUploadedFiles((state) =>
      state.map((file) => (file.id === id ? { ...file, ...data } : file))
    )
  }, [])

  const processUpload = useCallback(
    async (uploadedFile: IFile) => {
      try {
        const data = new FormData()

        const fileName = normalizeFileName(uploadedFile.name)

        if (uploadedFile.file) {
          data.append('files', uploadedFile.file, fileName)
        }

        const response: any = await axios.post('orders/upload', data, {
          onUploadProgress: (progressEvent) => {
            const progress: number = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )

            updateFile(uploadedFile.id, { progress })
          }
        })
        updateFile(uploadedFile.id, {
          uploaded: true,
          name: fileName,
          ...response[0]
        })
      } catch (err) {
        console.log(err)
        console.error(`Error to trying send "${uploadedFile.name}" `)
        toast.error(
          `Não foi possível adicionar "${uploadedFile.name}" como anexo`
        )
        updateFile(uploadedFile.id, {
          error: true
        })
      }
    },
    [updateFile]
  )

  const handleUpload = useCallback(
    (files: File[]) => {
      const newUploadedFiles: IFile[] = files.map((file: File) => ({
        file,
        id: uniqueId(),
        name: file.name,
        readableSize: fileSize(file.size),
        path: '',
        filename: '',
        progress: 0,
        uploaded: false,
        error: false,
        url: ''
      }))

      setUploadedFiles((prev) => prev.concat(newUploadedFiles))
      newUploadedFiles.forEach(processUpload)
    },
    [processUpload]
  )

  const deleteFile = useCallback(
    async (id: string) => {
      const file = uploadedFiles.find((file) => file.id === id && !file.error)
      try {
        if (file) await axios.delete(`orders/upload/${file.filename}`)
        setUploadedFiles((prev) => prev.filter((file) => file.id !== id))
      } catch (err) {
        console.log(err)
        toast.error('Não foi possível remover o anexo!')
      }
    },
    [uploadedFiles]
  )

  return (
    <FileContext.Provider value={{ uploadedFiles, deleteFile, handleUpload }}>
      {children}
    </FileContext.Provider>
  )
}

function useFiles(): IFileContextData {
  const context = useContext(FileContext)

  if (!context) {
    throw new Error('useFiles must be used within FileProvider')
  }

  return context
}

export { FileUploadProvider, useFiles }
